import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as moment from "moment";
import { Observable } from "rxjs";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import { forkJoin } from "rxjs/observable/forkJoin";
import { of } from "rxjs/observable/of";
import { catchError } from "rxjs/operators/catchError";
import { map } from "rxjs/operators/map";
import { AuthService } from "../../app/auth/auth.service";
import {
  NO_CHECK_CREDENTIALS_TYPES,
  NO_CHECK_HOSPICE_TEAM_ROLE,
  VALID_CREDENTIALS_TYPES,
} from "../../configs/credentialsTypes";
import { LookupValue } from "../../models/common/lookup-value";
import ActivityGridItem from "../../models/company-resources/activity-grid";
import CompanyResource from "../../models/company-resources/companyResource";
import { BusinessPhoneNumberType } from "../../models/contact/business-phone-number";
import ContentWrapper from "../../models/content-wrapper/content-wrapper";
import SortOptions from "../../models/content-wrapper/sort-options";
import Activity from "../../models/patient-chart/activities/activity";
import CompanyResourceSummary from "../../models/resource-management/company-resource-summary";
import {
  isProfessionalProvider,
  isVolunteer,
} from "../../models/resource-management/company-resource-type-guards";
import CompanyResourceType from "../../models/resource-management/company-resource-type.enum";
import ContactSearchResult from "../../models/resource-management/contact-search";
import Credential from "../../models/resource-management/credential";
import CredentialedResource from "../../models/resource-management/credentialed-resource";
import Physician from "../../models/resource-management/physician";
import PhysicianOrder from "../../models/resource-management/physician-order.model";
import ProfessionalProvider from "../../models/resource-management/professional-provider";
import isVolunteerPreferences from "../../models/resource-management/resource-schedule-guard";
import Volunteer from "../../models/resource-management/volunteers/volunteer";
import VolunteerMatchTerm from "../../models/resource-management/volunteers/volunteer-match-term";
import VolunteerSchedule from "../../models/resource-management/volunteers/volunteer-schedule";
import RoleUpdate from "../../models/system-admin/role-update";
import { WorkforceContact } from "../../models/system-admin/workforce-contact";
import { ConfigurationService } from "../configuration/configuration.service";

const OTHER_CREDENTIALS = "Other";

@Injectable()
export class ResourceManagementService {
  private readonly resourceManagementUrl: string;
  private readonly headers;

  private messageUserEmailAddress = new BehaviorSubject<string>("");
  systemUserEmailAddress = this.messageUserEmailAddress.asObservable();

  public companyId = 0;
  selectedOrgType: string | undefined;

  constructor(
    public http: HttpClient,
    private config: ConfigurationService,
    private authSvc: AuthService
  ) {
    if (this.config.getConfig().isLocal) {
      this.resourceManagementUrl =
        config.getConfig().localEndpoints.resourceManagement;
    } else {
      this.resourceManagementUrl = config.getConfig().apiUrl;
    }

    this.headers = new HttpHeaders().set("Content-Type", "application/json");
  }

  //getCredentialsByExternalId
  /**
   * Returns a list of valid credentials
   * @param listOfExternalIds array of numbers
   */
  getValidCredentialsByExternalId(listOfExternalIds: number[]) {
    return this.http.post<any>(
      `${this.resourceManagementUrl}/company-resources/providers/credentials`,
      listOfExternalIds
    );
  }

  // region Operations
  /**
   * Returns a list of volunteer match terms
   * @param scoped true/false on whether to scope by tenant or just grab the default
   */
  getVolunteerMatchTerms(scoped: boolean): Observable<VolunteerMatchTerm[]> {
    const s = scoped ? "true" : "false";
    return this.http.get<VolunteerMatchTerm[]>(
      `${this.resourceManagementUrl}/company-resources/volunteers/match-terms?tenant-scoped=${s}`
    );
  }

  updatePhysicianOrder(
    patientChartId: number,
    physicianOrder: PhysicianOrder
  ): Observable<Object> {
    return this.http.put(
      `${this.resourceManagementUrl}/patient-charts/${patientChartId}/physician-orders`,
      physicianOrder
    );
  }

  getPhysicianOrder(patientChartId: number): Observable<PhysicianOrder> {
    return this.http
      .get<PhysicianOrder>(
        `${this.resourceManagementUrl}/patient-charts/${patientChartId}/physician-orders`
      )
      .map((x) => {
        return new PhysicianOrder(x.patientChartId, x.physicianOrder);
      });
  }

  getPersons(qbosid: any): Observable<any> {
    let utcdate = moment().format();
    return this.http
      .get(
        `${this.resourceManagementUrl}/company-resources/persons/${qbosid}?credentialValidDatetime=` +
          utcdate
      )
      .map((x) => {
        return x;
      });
  }

  /* Return all available lookup values */
  getLookUpValues() {
    return this.http.get(
      `${this.resourceManagementUrl}/company-resources/lookups`
    );
  }

  getJobRoleDetails() {
    return this.http.get(
      `${this.resourceManagementUrl}/company-resources/job-role-details`
    );
  }

  createPhysician(patientChartId: number, physician: Physician) {
    return this.http.post(
      `${this.resourceManagementUrl}/patient-charts/${patientChartId}/contacts`,
      physician
    );
  }

  searchPhysicians(query: string): Observable<Physician[]> {
    return this.http
      .get<Physician[]>(
        `${this.resourceManagementUrl}/company-resources/physicians?q=${query}`
      )
      .map((x) => this.mapPhysician(x));
  }

  searchCommunityResources(query: string): Observable<Physician[]> {
    return this.http
      .get<Physician[]>(
        `${this.resourceManagementUrl}/patient-charts/relation/community-resources?q=${query}`
      )
      .map((x) => this.mapPhysician(x));
  }

  getPhysicians(patientChartId: number): Observable<Physician[]> {
    return this.http
      .get<Physician[]>(
        `${this.resourceManagementUrl}/patient-charts/${patientChartId}/physicians`
      )
      .map((x) => this.mapPhysician(x));
  }

  getPhysicianByNpi(npi: string): Observable<Physician> {
    return this.http.get<Physician>(
      `${this.resourceManagementUrl}/company-resources/physicians/npi/${npi}`
    );
  }

  deletePhysician(
    patientChartId: number,
    physicianId: number,
    contactType: string = ""
  ) {
    return this.http.delete(
      `${this.resourceManagementUrl}/patient-charts/${patientChartId}/physicians/${physicianId}?type=${contactType}`
    );
  }

  getActivities(
    selectedPage: number,
    limit: number,
    disciplines: string[],
    activityTypes: number[],
    startDate: string,
    endDate: string,
    sort: string = "id",
    order: string = "desc"
  ): Observable<ContentWrapper<ActivityGridItem>> {
    // Every one of these filters used to be accepted here and then dropped on the
    // floor — only page and limit reached the wire — so the Activity Log grid's
    // discipline, activity-type, date-range and column-sort controls all did
    // nothing. resource-management-service's ActivityLogController has always
    // supported them (disciplines, activity-types, f, t, sort, order).
    const params: string[] = [`page=${selectedPage}`, `limit=${limit}`];
    (disciplines || [])
      .filter((d) => d !== null && d !== undefined && d !== "")
      .forEach((d) => params.push(`disciplines=${encodeURIComponent(d)}`));
    (activityTypes || [])
      .filter((t) => t !== null && t !== undefined)
      .forEach((t) =>
        params.push(`activity-types=${encodeURIComponent(String(t))}`)
      );
    if (startDate) {
      params.push(`f=${encodeURIComponent(startDate)}`);
    }
    if (endDate) {
      params.push(`t=${encodeURIComponent(endDate)}`);
    }
    params.push(`sort=${encodeURIComponent(sort)}`);
    params.push(`order=${encodeURIComponent(order)}`);
    return this.http.get<ContentWrapper<ActivityGridItem>>(
      `${
        this.resourceManagementUrl
      }/company-resources/employees/activities?${params.join("&")}`
    );
  }

  public createActivityLogEntry(Act: Activity): Observable<Activity> {
    return this.http.post<Activity>(
      `${this.resourceManagementUrl}/company-resources/employees/activities`,
      Act
    );
  }

  public getActivity(activityId: number): Observable<ActivityGridItem> {
    return this.http.get<ActivityGridItem>(
      `${this.resourceManagementUrl}/company-resources/employees/activities/${activityId}`
    );
  }

  public updateActivity(
    activityId: number,
    Act: Activity
  ): Observable<Activity> {
    return this.http.put<Activity>(
      `${this.resourceManagementUrl}/company-resources/employees/activities/${activityId}`,
      Act
    );
  }

  /**
   * Returns a list of company resource summaries as specified by the search
   * @param page the page to pull results on
   * @param limit the number of results to return
   * @param query the query to filter the results by
   * @param sort the column/direction of the results
   * @param contactType the type of contacts to filter upon
   * @param activityLog
   * @Param nonSystemUsersEnabled
   */
  public getCompanyResourceSummaries(
    page: number = 0,
    limit: number = 10,
    query: string = "",
    sort: SortOptions,
    contactType: CompanyResourceType,
    activityLog: string = "",
    nonSystemUsersEnabled = false
  ): Observable<ContentWrapper<CompanyResourceSummary>> {
    return this.http
      .get<ContentWrapper<CompanyResourceSummary>>(
        `${this.resourceManagementUrl}/company-resources?q=${query}&page=${page}&limit=${limit}&sort=${sort.column}&order=${sort.direction}&contact-type=${CompanyResourceType[contactType]}&mustField.systemUsers=${nonSystemUsersEnabled}`
      )
      .map((x) => {
        return this.mapCompanyResourceSummaries(x);
      });
  }

  /**
   * Returns a list of company resource summaries as specified by the search/filters
   * @param page the page to pull results on
   * @param limit the number of results to return
   * @param query the query to filter the results by
   * @param sort the column/direction of the results
   * @param contactType the type of contacts to filter upon
   * @param activityLog
   * @param nonCredentialEnabled  filters in Workforce RM
   * @param nonSystemUsersEnabled  filters in Workforce RM
   * @param location
   * @param professionalRelation
   * @param companyId
   * @param showInactiveUsers
   * @param discipline
   */
  public getWorkforceNonVolunteerFilter(
    page: number = 0,
    limit: number = 10,
    query: string = "",
    sort: SortOptions,
    contactType: CompanyResourceType,
    activityLog: string = "",
    nonCredentialEnabled: boolean,
    nonSystemUsersEnabled: boolean,
    location: string,
    professionalRelation: LookupValue,
    companyId: number = 0,
    showInactiveUsers: boolean = false,
    discipline: LookupValue[] = null,
    // OPS-2858 tri-state, mirroring the backend filter:
    //   null  = omit the param entirely; the server applies no filter
    //   true  = only support users     -> the Support Users tab
    //   false = exclude support users  -> the Non-Providers tab
    // Omitting it when null matters: every other caller of this method must keep
    // its existing behaviour.
    supportUsers: boolean = null
  ): Observable<ContentWrapper<CompanyResourceSummary>> {
    let url =
      `${this.resourceManagementUrl}/company-resources` +
      `?q=${query}&page=${page}&limit=${limit}&sort=${sort.column}&order=${sort.direction}&contact-type=${CompanyResourceType[contactType]}&activity-log=${activityLog}` +
      `&mustField.nonCredential=${nonCredentialEnabled}&mustField.systemUsers=${nonSystemUsersEnabled}`;

    if (location !== "") {
      url += `&mustField.location=${location}`;
    }

    if (
      professionalRelation &&
      professionalRelation.id !== undefined &&
      professionalRelation.id !== null
    ) {
      url += `&mustField.professionalRelation=${professionalRelation.id}`;
    }

    if (discipline && discipline.length > 0) {
      url += `&mustField.discipline=${discipline.map((x) => x.id).join(",")}`;
    }

    url += `&mustField.inactiveUsers=${showInactiveUsers}`;

    if (supportUsers !== null && supportUsers !== undefined) {
      url += `&mustField.supportUsers=${supportUsers}`;
    }

    if (companyId > 0) {
      url += `&companyId=${companyId}`;
    }

    return this.http
      .get<ContentWrapper<CompanyResourceSummary>>(url)
      .map((x) => {
        return this.mapCompanyResourceSummaries(x);
      });
  }

  /**
   * Returns a list of profession provider summaries as specified by the search
   * @param page the page to pull results on
   * @param limit the number of results to return
   * @param query the query to filter the results by
   * @param sort the column/direction of the results
   */
  public getProfessionalProviderSummaries(
    page: number = 0,
    limit: number = 10,
    query: string = "",
    sort: SortOptions
  ): Observable<ContentWrapper<CompanyResourceSummary>> {
    return this.http
      .get<ContentWrapper<CompanyResourceSummary>>(
        `${this.resourceManagementUrl}/company-resources/providers?q=${query}&page=${page}&limit=${limit}&sort=${sort.column}&order=${sort.direction}`
      )
      .map((x) => {
        return this.mapCompanyResourceSummaries(x);
      });
  }

  /**
   * Returns the professional provider by the given provider id
   * @param providerId
   */
  public getProfessionalProvider(
    providerId: number
  ): Observable<ProfessionalProvider> {
    return this.http
      .get<ProfessionalProvider>(
        `${this.resourceManagementUrl}/company-resources/providers/${providerId}`
      )
      .map((x) => {
        return ResourceManagementService.mapProfessionalProvider(x);
      });
  }

  /**
   * Returns the professional provider by the given provider id
   * @param providerId
   */
  public getProfessionalExternalId(
    providerId: number
  ): Observable<ProfessionalProvider> {
    return this.http
      .get<ProfessionalProvider>(
        `${this.resourceManagementUrl}/company-resources/${providerId}`
      )
      .map((x) => {
        return ResourceManagementService.mapProfessionalProvider(x);
      });
  }

  private mapCredentialedResource(credentialedResource: CredentialedResource) {
    if (isProfessionalProvider(credentialedResource)) {
      return ResourceManagementService.mapProfessionalProvider(
        credentialedResource
      );
    } else if (isVolunteer(credentialedResource)) {
      return ResourceManagementService.mapVolunteer(credentialedResource);
    } else {
      let lc = new CredentialedResource();
      ResourceManagementService.mapLicensedClinician(lc, credentialedResource);
      return lc;
    }
  }

  /**
   * Returns the licensed clinician by the given id
   * @param id
   */
  public getCredentialedResource(id: number): Observable<CredentialedResource> {
    this.validateUserToken();
    let url = `${this.resourceManagementUrl}/company-resources/${id}`;
    if (this.companyId > 0) {
      url = `${this.resourceManagementUrl}/company-resources/${id}?companyId=${this.companyId}`;
    }
    return this.http
      .get<ProfessionalProvider>(url)
      .map(this.mapCredentialedResource);
  }

  /**
   * Returns the licensed clinician by the given externalId
   * @param externalId
   */
  public getCredentialedResourceByExternalId(
    externalId: number
  ): Observable<CredentialedResource> {
    return this.http
      .get<ProfessionalProvider>(
        `${
          this.resourceManagementUrl
        }/company-resources/${externalId}?useExternalId=true&companyId=${+this.authSvc.getCompany()
          .companyId}`
      )
      .map(this.mapCredentialedResource);
  }

  public getNameForSignatureByExternalId(
    externalId: number
  ): Observable<String> {
    return this.http
      .get<ProfessionalProvider>(
        `${
          this.resourceManagementUrl
        }/company-resources/${externalId}?useExternalId=true&companyId=${+this.authSvc.getCompany()
          .companyId}`
      )
      .map(this.mapCredentialedResource)
      .map((data) => {
        const { firstName, lastName, middleInitial } = data;
        return `${firstName} ${
          middleInitial ? `${middleInitial} ` : ""
        }${lastName}`;
      });
  }

  /**
   * Creates a new professional provider
   * @param provider the payload for the provider
   */
  public createProfessionalProvider(
    provider: CompanyResource
  ): Observable<CompanyResource> {
    return this.http.post<CompanyResource>(
      `${this.resourceManagementUrl}/company-resources`,
      provider
    );
  }

  /**
   * Updates the supplied provider by id
   * @param id the id of the provider
   * @param provider the updated values for the provider
   */
  public updateProfessionalProvider(
    id: number,
    provider: CompanyResource
  ): Observable<CompanyResource> {
    return this.http.put<CompanyResource>(
      `${this.resourceManagementUrl}/company-resources/${id}`,
      provider
    );
  }

  /**
   * Checks for duplicate NPI
   * @param npi the npi to check for
   */
  public checkDuplicateProvider(npi: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.resourceManagementUrl}/company-resources/providers/provider-exists/${npi}`
    );
  }

  /**
   * Returns the professional provider by the given external id
   * @param externalId
   */
  public getProviderByExternalId(
    externalId: number
  ): Observable<ProfessionalProvider> {
    let url = `${
      this.resourceManagementUrl
    }/company-resources/${externalId}?useExternalId=true&companyId=${+this.authSvc.getCompany()
      .companyId}`;
    return this.http.get<ProfessionalProvider>(url).map((x) => {
      return ResourceManagementService.mapProfessionalProvider(x);
    });
  }

  public updateRoles(roleUpdate: RoleUpdate): Observable<RoleUpdate> {
    return this.http.put<RoleUpdate>(
      `${this.resourceManagementUrl}/company-resources/roles`,
      roleUpdate
    );
  }

  // endregion

  // TODO extract this code -- BBP
  // region Mappers and converters
  private mapPhysician(response): Physician[] {
    let mapped = [];

    response.forEach((physician) => {
      let p = new Physician();
      p.id = physician.id;
      p.contactId = physician.contactId;
      p.address = physician.address;
      p.work = physician.work;
      p.mobile = physician.mobile;
      p.fax = physician.fax;
      p.npi = physician.npi;
      p.firstName = physician.firstName;
      p.lastName = physician.lastName;
      p.contactType = physician.contactType;
      p.emailAddress = physician.emailAddress;
      p.situationNotes = physician.situationNotes;
      p.relationType = physician.relationType;
      p.referralType = physician.referralType;
      p.referralCreditType = physician.referralCreditType;
      p.selectedCredential = physician.selectedCredential;
      mapped.push(p);
    });

    return mapped;
  }

  /**
   * Map a volunteer from incoming payload
   * @param volunteer the mapped volunteer
   * @private
   */
  private static mapVolunteer(volunteer: Volunteer): Volunteer {
    let v = new Volunteer(
      volunteer.languages,
      volunteer.volunteerType,
      volunteer.trainingCompletionDate,
      volunteer.title,
      volunteer.preferences
    );

    if (
      volunteer.preferences &&
      isVolunteerPreferences(volunteer.preferences)
    ) {
      v.preferences.schedule = new VolunteerSchedule(
        volunteer.preferences.schedule.availabilities,
        volunteer.preferences.schedule.locationPreferences
      );
      v.preferences.skillsAndInterests =
        volunteer.preferences.skillsAndInterests;
    }

    ResourceManagementService.mapLicensedClinician(v, volunteer);
    return v;
  }

  /**
   * Map provider details.
   * @param provider
   */
  private static mapProfessionalProvider(
    provider: ProfessionalProvider
  ): ProfessionalProvider {
    let p = new ProfessionalProvider();
    p.npi = provider.npi;
    p.dea = provider.dea;
    p.deaStartDate = provider.deaStartDate;
    p.deaExpireDate = provider.deaExpireDate;
    p.resourceStatus = provider.resourceStatus;
    p.taxId = provider.taxId;
    p.attachedToPatients = provider.attachedToPatients;
    p.taxonomyCode = provider.taxonomyCode;
    p.groupAffiliation = provider.groupAffiliation;
    ResourceManagementService.mapLicensedClinician(p, provider);
    return p;
  }

  /**
   * Map licensed clinician details.
   * @param clinician the one to map into
   * @param incoming the one with the data
   */
  private static mapLicensedClinician(
    clinician: CredentialedResource,
    incoming: CredentialedResource
  ): void {
    clinician.resourceType = incoming.resourceType;
    clinician.id = incoming.id;
    clinician.firstName = incoming.firstName;
    clinician.lastName = incoming.lastName;
    clinician.preferredName = incoming.preferredName;
    clinician.middleInitial = incoming.middleInitial;
    clinician.resourceStatus = incoming.resourceStatus;
    clinician.emailAddress = incoming.emailAddress;
    clinician.tenantId = incoming.tenantId;
    clinician.addresses = incoming.addresses;
    clinician.phoneNumbers = incoming.phoneNumbers;
    clinician.externalId = incoming.externalId;
    clinician.isEmployee = incoming.isEmployee;
    clinician.professionalRelation = incoming.professionalRelation;
    clinician.notes = incoming.notes;
    clinician.gender = incoming.gender;
    clinician.dateOfBirth = incoming.dateOfBirth;
    clinician.primaryLanguage = incoming.primaryLanguage;
    clinician.professionalRelation = incoming.professionalRelation;
    clinician.groupAffiliation = incoming.groupAffiliation;
    clinician.userId = incoming.userId;
    clinician.accountName = incoming.accountName;
    clinician.suffix = incoming.suffix;
    clinician.effectiveDate = incoming.effectiveDate;
    clinician.employedBy = incoming.employedBy;
    clinician.deaStartDate = incoming.deaStartDate;
    clinician.deaExpireDate = incoming.deaExpireDate;
    clinician.cognitoUserId = incoming.cognitoUserId;
    clinician.companyId = incoming.companyId ? incoming.companyId : "0";
    clinician.companyName = incoming.companyName
      ? incoming.companyName
      : "test";
    clinician.isSystemUser = !!incoming.isSystemUser;
    // OPS-2858. This mapper hand-copies 38 fields, so a new one is dropped unless it
    // is listed here. Without it, data.isSupportUser is undefined in
    // workforce-contact.loadData -> contactIsSupportUser stays false -> the widened
    // org picker never renders and the save path writes isSupportUser=false,
    // silently reclassifying the user out of the Support Users tab.
    clinician.isSupportUser = !!incoming.isSupportUser;

    clinician.credentials = [];
    if (incoming.credentials) {
      for (let i = 0; i < incoming.credentials.length; i++) {
        let y = incoming.credentials[i];
        let c = new Credential();
        c.id = y.id;
        c.type = y.type;
        c.licenseId = y.licenseId;
        c.issueState = y.issueState;
        c.validToDatetime = y.validToDatetime;
        c.validFromDatetime = y.validFromDatetime;
        c.operatingAs = y.operatingAs;
        c.other = y.other;
        c.deleted = y.deleted;
        c.active = y.active;
        c.createdDatetime = y.createdDatetime;
        c.updatedDatetime = y.updatedDatetime;
        c.createdBy = y.createdBy;
        c.updatedBy = y.updatedBy;

        clinician.credentials.push(c);
      }
    }
    if (incoming.employeeInformation) {
      clinician.employeeInformation = incoming.employeeInformation;
    }
    if (incoming.contactLocations) {
      clinician.contactLocations = incoming.contactLocations;
    }
    if (incoming.phoneNumbers && incoming.phoneNumbers.length > 0) {
      clinician.workPhoneNumbers = incoming.phoneNumbers
        .filter((x) => x.phoneNumberType === BusinessPhoneNumberType.WORK)
        .sort((a, b) => a.id - b.id);
      clinician.faxPhoneNumbers = incoming.phoneNumbers
        .filter((x) => x.phoneNumberType === BusinessPhoneNumberType.FAX)
        .sort((a, b) => a.id - b.id);

      clinician.cellPhoneNumbers = incoming.phoneNumbers
        .filter((x) => x.phoneNumberType === BusinessPhoneNumberType.CELL)
        .sort((a, b) => a.id - b.id);
    }
  }

  /**
   * Maps and returns company resource summaries
   * @param x the incoming payload from the api
   */
  private mapCompanyResourceSummaries(
    x: ContentWrapper<CompanyResourceSummary>
  ): ContentWrapper<CompanyResourceSummary> {
    let c = new ContentWrapper<CompanyResourceSummary>();

    c.totalElements = x.totalElements;
    c.totalPages = x.totalPages;
    c.pageable = x.pageable;
    c.size = x.size;
    c.content = [];

    x.content.forEach((s) => {
      let summary: CompanyResourceSummary = {
        id: s.id,
        externalId: s.externalId,
        firstName: s.firstName,
        lastName: s.lastName,
        middleInitial: s.middleInitial,
        credentials: s.credentials,
        displayName: s.displayName,
        createdDatetime: s.createdDatetime,
        npi: s.npi,
        dea: s.dea,
        isSelected: s.isSelected,
        isLoaded: s.isLoaded,
        phoneNumber: s.phoneNumber,
        faxNumber: s.faxNumber,
        trainingCompletionDate: s.trainingCompletionDate,
        status: s.status,
        hasCredentials: s.hasCredentials,
        createdBy: s.createdBy,
        deaExpireDate: s.deaExpireDate,
        $provider: null,
        locations: this.mapLocationsObject(s.locations),
        professionalRelation: s.professionalRelation,
        cognitoUserId: s.cognitoUserId ? s.cognitoUserId : null,
        isSystemUser: s.isSystemUser ? s.isSystemUser : false,
        disciplines: s.disciplines ? s.disciplines : null,
        addresses: s.addresses ? s.addresses : [],
        fax: s.fax ? s.fax : [],
      };
      c.content.push(summary);
    });

    return c;
  }

  private mapLocationsObject(locations: string): string {
    let objectLocations = JSON.parse(locations);
    if (objectLocations && objectLocations.length)
      return objectLocations.map((l) => l.displayCode).join(", ");
    return "";
  }

  // endregion

  public getCompanyResourceProvidersAndNonProviders(
    page: number = 0,
    limit: number = 10,
    query: string = "",
    sort: SortOptions,
    nonSystemUsersEnabled = false
  ): Observable<ContentWrapper<CompanyResourceSummary>[]> {
    let providers = this.getCompanyResourceSummaries(
      page,
      limit,
      query,
      sort,
      CompanyResourceType.PROVIDER,
      "",
      nonSystemUsersEnabled
    );
    let nonProviders = this.getCompanyResourceSummaries(
      page,
      limit,
      query,
      sort,
      CompanyResourceType.NON_PROVIDER_CLINICIAN,
      "",
      nonSystemUsersEnabled
    );

    return forkJoin([providers, nonProviders]);
  }

  public getContactSearch(q: string): Observable<ContactSearchResult[]> {
    return this.http
      .get<ContentWrapper<CompanyResourceSummary>>(
        `${this.resourceManagementUrl}/company-resources?q=${q}&activity-log=activity`
      )
      .map((x) => {
        let cs: ContactSearchResult[] = [];
        for (let i = 0; i < x.content.length; i++) {
          cs.push(this.mapContactSearch(x.content[i]));
        }
        return cs;
      });
  }

  public getContactActivityLogSearch(
    q: string,
    isEmployee: boolean = false
  ): Observable<WorkforceContact[]> {
    return this.http
      .get<WorkforceContact[]>(
        `${this.resourceManagementUrl}/company-resources/search?q=${q}&isEmployee=${isEmployee}`
      )
      .map((results) => {
        results = results.map((x) => {
          return this.mapWorkForceContact(x);
        });
        return results;
      });
  }

  private mapWorkForceContact(x: WorkforceContact) {
    let middleInitial = x.middleInitial ? x.middleInitial : "";
    let userName = `${x.lastName}, ${x.firstName} ${middleInitial}`.trim();
    x = {
      ...x,
      middleInitial: middleInitial,
      userName: userName,
      displayName: `${userName} - ${x.externalId}`,
    };
    return x;
  }

  private mapContactSearch(companyResourceSummary: CompanyResourceSummary) {
    let contactSearch = new ContactSearchResult();
    contactSearch.id = companyResourceSummary.id;
    contactSearch.firstName = companyResourceSummary.firstName;
    contactSearch.lastName = companyResourceSummary.lastName;
    contactSearch.contactType = "0";
    return contactSearch;
  }

  changeEmailAddress(email: string) {
    this.messageUserEmailAddress.next(email);
  }

  public loadTaxonomy(): Observable<any> {
    return this.http.get("./assets/taxonomy.json");
  }

  public validateUserToken() {
    this.companyId = 0;
    this.selectedOrgType = this.authSvc.getUser().selectedOrgType;
    if (this.selectedOrgType && this.selectedOrgType === "COMPANY") {
      this.companyId = +this.authSvc.getCompany().companyId;
    }
  }

  getCredentialDetails(
    patientId: number,
    credentialsTypes: any[],
    selectedRole: string,
    username: string,
    startDate: Date,
    endDate: Date,
    orderDiscontinueDate: Date,
    startVisit: boolean,
    isUser: boolean
  ): Observable<{ response: string; disableSaveButton: boolean }> {
    return new Observable((observer) => {
      this.getCredentialedResourceByExternalId(patientId).subscribe((value) => {
        const matchedLicenses = value["credentials"].map((license) => {
          const matchingDescription = credentialsTypes.find(
            (desc) => desc.id === license.type
          );
          return {
            credential: matchingDescription
              ? matchingDescription["description"]
              : null,
            expirationTime: license["validToDatetime"]
              ? license["validToDatetime"]
              : null,
            otherCredential: license["other"] ? license["other"] : null,
          };
        });
        if (NO_CHECK_HOSPICE_TEAM_ROLE.has(selectedRole)) {
          observer.next({ response: null, disableSaveButton: false });
          observer.complete();
          return;
        }
        if (matchedLicenses.length === 0) {
          observer.next({
            response: `Please Note: the credentials for ${username} do not exist. Update the information or select a different caregiver.`,
            disableSaveButton: true,
          });
          observer.complete();
          return;
        }

        let response = null;
        let disableSaveButton = false;

        for (const license of matchedLicenses) {
          const key =
            license.credential !== OTHER_CREDENTIALS
              ? `${license.credential}_${selectedRole}`
              : `${license.credential}`;
          if (VALID_CREDENTIALS_TYPES.has(key)) {
            const expirationDate = license.expirationTime
              ? moment.utc(license.expirationTime).toDate()
              : null;
            const discontinueDate = orderDiscontinueDate
              ? moment.utc(orderDiscontinueDate).toDate()
              : moment.utc(endDate).toDate();

            const expirationMoment = expirationDate
              ? moment.utc(expirationDate)
              : null;
            const startMoment = moment.utc(startDate);
            const discontinueMoment = moment.utc(discontinueDate);

            const hasValidExpiration =
              !!expirationMoment && expirationMoment.isValid();
            const hasValidStart = startMoment.isValid();
            const hasValidDiscontinue = discontinueMoment.isValid();
            if (isUser) {
              if (
                (hasValidExpiration &&
                  hasValidStart &&
                  expirationMoment.isBefore(startMoment)) ||
                (hasValidExpiration &&
                  hasValidDiscontinue &&
                  expirationMoment.isBefore(discontinueMoment))
              ) {
                response = `The assigned user’s credentials are not current. Please update the credentials or assign a different user.`;
                disableSaveButton = true;
              } else {
                disableSaveButton = false;
                response = null;
                break;
              }
            } else if (startVisit) {
              if (
                hasValidExpiration &&
                hasValidStart &&
                expirationMoment
                  .startOf("day")
                  .isBefore(startMoment.startOf("day"))
              ) {
                response = `Please Note: the credentials for ${username} ${
                  key === OTHER_CREDENTIALS && license.otherCredential
                    ? license.otherCredential
                    : VALID_CREDENTIALS_TYPES.get(key).credential
                } expired on ${moment
                  .utc(expirationDate)
                  .format(
                    "MMMM D, YYYY"
                  )}. Update the information or select a different caregiver.`;
                disableSaveButton = true;
              } else {
                disableSaveButton = false;
                response = null;
                break;
              }
            } else {
              if (
                hasValidExpiration &&
                hasValidStart &&
                expirationMoment
                  .startOf("day")
                  .isBefore(startMoment.startOf("day"))
              ) {
                response = `Please Note: the credentials for ${username} ${
                  key === OTHER_CREDENTIALS && license.otherCredential
                    ? license.otherCredential
                    : VALID_CREDENTIALS_TYPES.get(key).credential
                } expired on ${moment
                  .utc(expirationDate)
                  .format(
                    "MMMM D, YYYY"
                  )}. Update the information or select a different caregiver.`;
                disableSaveButton = true;
              } else if (
                hasValidExpiration &&
                hasValidDiscontinue &&
                expirationMoment.isBefore(discontinueMoment)
              ) {
                response = `Please Note: the credentials for ${username} ${
                  key === OTHER_CREDENTIALS && license.otherCredential
                    ? license.otherCredential
                    : VALID_CREDENTIALS_TYPES.get(key).credential
                } expires on ${moment
                  .utc(expirationDate)
                  .format(
                    "MMMM D, YYYY"
                  )}. Update the information or select a different caregiver.`;
                disableSaveButton = false;
              } else {
                disableSaveButton = false;
                response = null;
                break;
              }
            }
          } else if (NO_CHECK_CREDENTIALS_TYPES.has(license.credential)) {
            disableSaveButton = false;
            response = null;
            break;
          } else {
            response = `Please Note: the credentials for ${username} do not exist. Update the information or select a different caregiver.`;
            disableSaveButton = true;
          }
        }

        observer.next({ response, disableSaveButton });
        observer.complete();
      });
    });
  }

  getCredentialsByPatientId(
    patientId: number,
    formType: "fg" | "scheduleForm",
    assignedToId: number,
    users: any[],
    credentialsTypes: any[],
    companyRoles: any[],
    formControls: any,
    unassignedVisit: any = null
  ): Observable<{ response: string; disableSaveButton: boolean }> {
    return new Observable((observer) => {
      if (patientId) {
        const selectedUser = users.find((user) => user.id === assignedToId);
        const username = selectedUser ? selectedUser.username : "";

        const roleControl = formType === "fg" ? "disciplineCode" : "role";
        const selectedRole =
          formType === "fg"
            ? companyRoles.find(
                (role) => role.lookup === formControls[roleControl].value
              ).display
            : formControls[roleControl].value;

        const startDate = unassignedVisit
          ? moment.utc(unassignedVisit.orderStartDate).toDate()
          : moment.utc(formControls["startDate"].value).toDate();

        const endDate = unassignedVisit
          ? moment.utc(unassignedVisit.orderDiscontinueDate).toDate()
          : moment.utc(formControls["endDate"].value).toDate();

        this.getCredentialDetails(
          patientId,
          credentialsTypes,
          selectedRole,
          username,
          startDate,
          endDate,
          unassignedVisit ? unassignedVisit.orderDiscontinueDate : null,
          false,
          false
        ).subscribe(({ response, disableSaveButton }) => {
          observer.next({ response, disableSaveButton });
          observer.complete();
        });
      } else {
        observer.complete();
      }
    });
  }

  /**
   * Returns provider information with resolved credential descriptions
   * @param externalId the external ID of the provider
   */
  public getProviderWithCredentialDescriptions(externalId: number): Observable<{
    firstName: string;
    middleName: string;
    lastName: string;
    credentialDescriptions: string[];
  }> {
    return forkJoin([
      this.getLookUpValues(),
      this.getProviderByExternalId(externalId),
    ]).pipe(
      map(([lookupValues, provider]: [any, ProfessionalProvider]) => {
        const credentialDescriptions = provider.credentials
          .map((credential) => {
            const lookupElement = lookupValues["CredentialType"]?.find(
              (lookup) => lookup.id === credential.type
            );
            return lookupElement ? lookupElement.description : "";
          })
          .filter((desc) => desc !== ""); // Remove empty descriptions

        return {
          firstName: provider.firstName,
          lastName: provider.lastName,
          credentialDescriptions,
          ...(provider.middleInitial &&
            provider.middleInitial.trim() && {
              middleName: provider.middleInitial,
            }),
        };
      }),
      catchError((error) => {
        console.error("Error fetching provider with credentials:", error);
        return of({
          firstName: "",
          middleName: "",
          lastName: "",
          credentialDescriptions: [],
        });
      })
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/resource-management/resource-management.service.ts